import type { FC } from 'react';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import SiteFooter from '@wix/da-shared-react/pkg/SiteFooter';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import { submitModalRenderer } from '@wix/da-submit-shared/pkg/project-tools';
import AboutPage from '../Pages/About';
import PolicyPage from '../Pages/Policy';
import { muroModalRenderer } from '@wix/da-muro-ui/pkg/project-tools';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';
import MobileMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mobile';
import DamzDrawerLoadable from '@wix/da-damz-shared/pkg/components/Drawer/DrawerLoadable';
import DamzPageLoadable from '@wix/da-damz-shared/pkg/components/DamzPage/DamzPageLoadable';
import DamzGlobal from '@wix/da-damz-shared/pkg/redux/ModuleLoader';
import { damzModalRenderer } from '@wix/da-damz-shared/pkg/project-tools';

import s from './App.scss';

const MuroDepValue = {
  MicroComponent: MicroMuro,
  MiniComponent: MiniMuro,
  MobileComponent: MobileMuro,
};

export interface Props {
  language: string;
  environment?: RuntimeEnvironment;
  page: string;
  appMounted: (pageName: string) => void;
}

const pageKeys = {
  policy_api: 'about:api',
  policy_copyright: 'about:copyright',
  policy_etiquette: 'about:etiquette',
  policy_points: 'about:points',
  policy_prints: 'about:prints',
  policy_privacy: 'about:privacy',
  policy_service: 'about:service',
  policy_submission: 'about:submission',
  policy_dreamup: 'about:dreamup',
  policy_accessibility: 'about:accessibility',
};
const pageComponents = {
  about: AboutPage,
};

const App: FC<Props> = ({ language, environment, page, appMounted }) => {
  useEffect(() => {
    appMounted('about');
  }, [appMounted]);

  let pageKey, PageComponent;
  if (pageKeys.hasOwnProperty(page)) {
    pageKey = pageKeys[page];
  } else if (pageComponents.hasOwnProperty(page)) {
    PageComponent = pageComponents[page];
  }

  return (
    <HelmetProvider>
      <MuroDepContext.Provider value={MuroDepValue}>
        <DaApp
          language={language}
          environment={environment}
          ncContextProps={{
            PageComponent: FeedbackPageLoadable,
            DrawerComponent: DrawerLoadable,
          }}
          damzContextProps={{
            GlobalComponent: DamzGlobal,
            DrawerComponent: DamzDrawerLoadable,
            PageComponent: DamzPageLoadable,
          }}
          modalsRenderFunc={composeModalRenderers(
            submitModalRenderer,
            muroModalRenderer,
            damzModalRenderer as any
          )}
        >
          <SiteHeader />
          <div className={s['content-container']}>
            {pageKey ? (
              <PolicyPage i18nKey={pageKey} />
            ) : PageComponent ? (
              <PageComponent />
            ) : (
              <p>Your {page} page would go here</p>
            )}
          </div>
          <SiteFooter />
        </DaApp>
      </MuroDepContext.Provider>
    </HelmetProvider>
  );
};
App.displayName = 'AboutApp';

export default App;
